import React, { createContext, useContext, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { PartnershipScenario, CalculatedResults } from '../types/types';
import { calculateROI } from '../utils/calculations';

interface PartnershipContextType {
    scenarios: PartnershipScenario[];
    addScenario: (scenario: Omit<PartnershipScenario, 'id'>) => PartnershipScenario;
    updateScenario: (id: string, scenario: Partial<PartnershipScenario>) => void;
    deleteScenario: (id: string) => void;
    getCalculatedResults: (id: string) => CalculatedResults | null;
    compareScenarios: (ids: string[]) => Array<{ scenario: PartnershipScenario; results: CalculatedResults }>;
}

const PartnershipContext = createContext<PartnershipContextType | undefined>(undefined);

export const usePartnership = () => {
    const context = useContext(PartnershipContext);
    if (!context) {
        throw new Error('usePartnership must be used within a PartnershipProvider');
    }
    return context;
};

export const PartnershipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [scenarios, setScenarios] = useState<PartnershipScenario[]>([]);

    const addScenario = useCallback((newScenario: Omit<PartnershipScenario, 'id'>) => {
        const scenarioWithId: PartnershipScenario = {
            ...newScenario,
            id: uuidv4()
        };
        setScenarios(prev => [...prev, scenarioWithId]);
        return scenarioWithId;
    }, []);

    const updateScenario = useCallback((id: string, updates: Partial<PartnershipScenario>) => {
        setScenarios(prev => prev.map(scenario => 
            scenario.id === id ? { ...scenario, ...updates } : scenario
        ));
    }, []);

    const deleteScenario = useCallback((id: string) => {
        setScenarios(prev => prev.filter(scenario => scenario.id !== id));
    }, []);

    const getCalculatedResults = useCallback((id: string) => {
        const scenario = scenarios.find(s => s.id === id);
        return scenario ? calculateROI(scenario) : null;
    }, [scenarios]);

    const compareScenarios = useCallback((ids: string[]) => {
        return scenarios
            .filter(scenario => ids.includes(scenario.id))
            .map(scenario => ({
                scenario,
                results: calculateROI(scenario)
            }));
    }, [scenarios]);

    const value = {
        scenarios,
        addScenario,
        updateScenario,
        deleteScenario,
        getCalculatedResults,
        compareScenarios
    };

    return (
        <PartnershipContext.Provider value={value}>
            {children}
        </PartnershipContext.Provider>
    );
}; 